import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';
import { WebStorage } from 'app/core/shared-services/web.storage';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public proxyUrl = environment.baseUrl;
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _webStorage: WebStorage,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        sessionStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return sessionStorage.getItem('accessToken') ?? '';
    }


  
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot username
     *
     * @param idNumber
     */
    forgotUsername(idNumber: string): Observable<any> {

        let headers = new HttpHeaders();
        headers = headers.append('IDNumber', idNumber);

        return this._httpClient.get(`${this.proxyUrl}/auth/forgotusername`, { headers });
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {

        let headers = new HttpHeaders();
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/forgotpassword`, { headers });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, token: string, email: string): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Token', token);
        headers = headers.append('Password', password);
        headers = headers.append('Username', email);

        return this._httpClient.get(`${this.proxyUrl}/auth/resetpassword`, { headers });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { Username: string; Password: string }): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Username', credentials.Username);
        headers = headers.append('Password', credentials.Password);
        headers = headers.append('AuthType', 'Customer');

        const options = {
            headers,
            responseType: 'text' as 'json'
        }
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.get(`${this.proxyUrl}/auth/token`, options).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response;
 

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                //this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
            catchError((error: any) => {
                // Ensure errors are properly propagated
                return throwError(() => error);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>
                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        sessionStorage.clear();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Reset in-memory session subjects
        this._webStorage.CartInfo.next(0);
        this._webStorage.StoredLead.next(0);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user - Registration fields. For OTP flow include RegisterWithOTPVerificationID and RegisterWithOTP.
     */
    signUp(
        user: {
            CustomerType: string;
            EmailAddress: string;
            FirstName: string;
            IDNumber: string;
            IDType: string;
            MobileNumber: string;
            Password: string;
            Surname: string;
            BusinessName?: string;
            BusinessRegistrationNumber?: string;
            BusinessVATNo?: string;
            RegisterWithOTPVerificationID?: string | number;
            RegisterWithOTP?: string;
        },
    ): Observable<any> {
        return this._httpClient.post(`${this.proxyUrl}/auth/register`, user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    /**
     * Check the authentication status
     */
    accountAvailability(EmailAddress: string, IDNumber: string, MobileUsername: number) {
        const data = {
            Data: {
                EmailAddress,
                IDNumber,
                MobileUsername
            },
            Url: 'endcustomer/validateavailability'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }

    /**
     * Returns true when the email is already linked to an existing customer account.
     */
    checkEmailAlreadyRegistered(email: string): Observable<boolean> {
        return this.accountAvailability(email, '', null).pipe(
            map((res: { IsAvailable?: boolean }) => res?.IsAvailable !== true),
            catchError((err) => {
                const message = err?.error?.Message ?? err?.error?.message ?? '';
                if (/existing customer record exists/i.test(String(message))) {
                    return of(true);
                }
                return of(false);
            })
        );
    }

    /**
     * Register MobileUsername and send OTP
     */
    registerUsernameAndSendOTP(EmailAddress: string,) {
        const data = {
            Data: {
                EmailAddress,
            },
            Url: 'endcustomer/validateavailability_generateLoginOTP'
        }

        return this._httpClient.post(`${this.proxyUrl}/service`, data);
    }
    
    /**
     * Request with attachments
     */
    registerWithAttachments(formData: FormData,) {
        return this._httpClient.post(`${this.proxyUrl}/auth/register/withAttachments`, formData);
    }

    /**
     * Request OTP to mobile number
     */
    requestMobileOTP(MobileUsername: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', MobileUsername);


        return this._httpClient.get(`${this.proxyUrl}/auth/requestMobileOTP`, { headers });
    }

    /**
     * Request OTP to email (mobileVerify flow – after register).
     * GET mobileVerifyRequestOTP?isEmail=true with headers MobileUsername (email), Name.
     * Returns response containing VerificationID for use in confirm step.
     */
    requestMobileOTPWithEmail(email: string, firstName: string): Observable<{ VerificationID?: string }> {
        const headers = new HttpHeaders()
            .append('MobileUsername', email)
            .append('Name', firstName);
        return this._httpClient.get<{ VerificationID?: string }>(
            `${this.proxyUrl}/auth/mobileVerifyRequestOTP?isEmail=true`,
            { headers }
        );
    }

    /**
     * Request OTP to mobile number (mobileVerify flow – after register).
     * GET mobileVerifyRequestOTP with headers MobileUsername (phone), Name.
     * Returns response containing VerificationID for use in confirm step.
     */
    requestMobileOTPWithMobile(mobileNumber: string, firstName: string): Observable<{ VerificationID?: string }> {
        const headers = new HttpHeaders()
            .append('MobileUsername', mobileNumber)
            .append('Name', firstName);
        return this._httpClient.get<{ VerificationID?: string }>(
            `${this.proxyUrl}/auth/mobileVerifyRequestOTP`,
            { headers }
        );
    }

    /**
     * Confirm email OTP (mobileVerify flow).
     * GET mobileVerifyConfirmOTP with headers Username, OTP, VerificationID.
     */
    emailVerifyConfirmOTP(email: string, otp: string, verificationID: string): Observable<string> {
        const headers = new HttpHeaders()
            .append('Username', email)
            .append('OTP', otp)
            .append('VerificationID', verificationID);
        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyConfirmOTP`, {
            headers,
            responseType: 'text',
        });
    }

    /**
     * Confirm mobile OTP (mobileVerify flow).
     * GET mobileVerifyConfirmOTP with headers MobileUsername, MobileOTP, VerificationID.
     */
    mobile_VerifyConfirmOTP(mobileNumber: string, otp: string, verificationID: string): Observable<string> {
        const headers = new HttpHeaders()
            .append('MobileUsername', mobileNumber)
            .append('MobileOTP', otp)
            .append('VerificationID', verificationID);
        return this._httpClient.get(`${this.proxyUrl}/auth/mobileVerifyConfirmOTP`, {
            headers,
            responseType: 'text',
        });
    }

    /**
     * Login using mobile OTP
     */
    loginWithOTP(MobileUsername: any, OTP: any) {
        let headers = new HttpHeaders();
        headers = headers.append('MobileUsername', MobileUsername);
        headers = headers.append('MobileOTP', OTP);


        //console.log({ headers })
        return this._httpClient.get(`${this.proxyUrl}/auth/loginWithMobileOTP`, { headers });
    }

     /**
     * Verify Email
     *
     * @param password
     */
     verifyEmailAddress(token: string): Observable<any> {

        return this._httpClient.get(`${this.proxyUrl}/service?url=endcustomer/verifyemailaddress?token=${token}`);
    }

    /**
     * Resend Email Verification
     *
    * @param Password - The user's password.
    * @param UserName - The user's email address.
     */
    resendVerification(Password: string, UserName: string): Observable<any> {

        let headers = new HttpHeaders();
        headers = headers
        // .append('Token', Token)
        .append('Password', Password)
        .append('UserName', UserName);

        return this._httpClient.get(`${this.proxyUrl}/auth/send-email-verification`, { headers });
    }

    /**
     * Resend Registration Link
     *
     * @param EmailAddress - The user's email address.
     */
    resendRegistrationLink(EmailAddress: string): Observable<any> {

        const data = {
            Url: `lead/forwardforcustomerregistration`,
            Data: { Username: EmailAddress }
        }

        return this._httpClient.put(`${this.proxyUrl}/service`, data);
    }




}
